import React, { forwardRef } from "react";

import { TextProvider, useTextContext } from "../../../context/Text";
import { TypographyProvider } from "../../../context/Typography";
import { useContentColor } from "../../../styles/colors/content/useContentColor";
import { ContentEmphasis, KeyColorRole } from "../../../styles/colors/types";
import {
  typographyBodyLineHeightMap,
  TypographyBodySize,
  typographyBodySizeMap,
  TypographyLineHeight
} from "../../../styles/typography/typography";
import { Box } from "../../layout";
import { capitalizeStyles, truncateStyles, uppercaseStyles } from "../consts";

import type {
  ResponsiveProp,
  FontWeight,
  TextAlign,
  Display
} from "../../../types";

type Props = {
  children: React.ReactNode | string;
  tone?: KeyColorRole;
  emphasis?: ContentEmphasis;
  onHighEmphasisSurface?: boolean;
  weight?: ResponsiveProp<FontWeight>;
  size?: TypographyBodySize;
  lineHeight?: TypographyLineHeight;
  display?: Display;
  align?: ResponsiveProp<TextAlign>;
  truncate?: boolean;
  uppercase?: boolean;
  capitalize?: boolean;
  dataNoTranslate?: boolean;
  dataTest?: string;
};

const Text = forwardRef<HTMLSpanElement | undefined, Props>(
  (
    {
      children,
      tone = "neutral",
      emphasis = "high",
      onHighEmphasisSurface = false,
      weight = "normal",
      size = "medium",
      lineHeight = "small",
      display = "block",
      align,
      truncate = false,
      uppercase = false,
      capitalize = false,
      dataNoTranslate,
      dataTest
    }: Props,
    ref
  ) => {
    if (useTextContext()) {
      throw new Error(
        "Text component cannot be a child of Typography components"
      );
    }

    const color = useContentColor({ tone, emphasis, onHighEmphasisSurface });

    return (
      <TextProvider value>
        <TypographyProvider value={{ typographyBodySize: size, lineHeight }}>
          <Box
            ref={ref}
            component="span"
            display={display}
            {...typographyBodySizeMap[size]}
            {...typographyBodyLineHeightMap[lineHeight]}
            fontWeight={weight}
            textAlign={align}
            margin="none"
            padding="none"
            color={color}
            dataNoTranslate={dataNoTranslate}
            dataTest={dataTest}
            {...(capitalize && capitalizeStyles)}
            {...(uppercase && uppercaseStyles)}
            {...(truncate && truncateStyles)}
          >
            {children}
          </Box>
        </TypographyProvider>
      </TextProvider>
    );
  }
);

export default Text;
